import { Feature, Overlay } from 'ol'
import Collection from 'ol/Collection'
import Point from 'ol/geom/Point'
import Vector from 'ol/layer/Vector'
import Map from 'ol/Map'
import { fromLonLat } from 'ol/proj'
import VectorSource from 'ol/source/Vector'
import { Fill, Stroke, Style, Text } from 'ol/style'
import React, { useEffect, useRef, useState } from 'react'
import { Icon } from 'semantic-ui-react'
import { IconSizeProp } from 'semantic-ui-react/dist/commonjs/elements/Icon/Icon'
import 'swiper/components/navigation/navigation.min.css'
import 'swiper/components/pagination/pagination.min.css'

import 'swiper/swiper.min.css'
import { MapUtils } from './map.utils'
import { LocationData } from './MapComponent'

export interface PopupProps<T> {
  data: T
  trigger: React.ReactNode
}

type MarkerPopup<T extends LocationData> = React.ComponentType<PopupProps<T>>

type Props = {
  map: Map | null,
  children: React.ReactNode
}

/**
 * Renders the markers on the maps as labels and icons
 * Assumes is will only be rendered once the map is ready, ie it calls addOverlay and addLayer immediately
 **/
export default function MapMarkers<T extends LocationData>({ map, children }: Props) {
  const [featuresCollection] = useState(new Collection<Feature>())

  //Add the vector layer for the labels
  useEffect(
    () => {
      if (map) {
        const vectorLayer = new Vector({
          className: 'ol-map-markers',
          source: new VectorSource({
              features: featuresCollection,
            },
          ),
        })

        map.addLayer(vectorLayer)
        return () => {
          map.removeLayer(vectorLayer)
        }
      }
    }, [map, featuresCollection],
  )

  const markersWithProps = React.Children.map(children, child => {
    // Checking isValidElement is the safe way and avoids a typescript
    // error too.
    if (React.isValidElement<MapMarkerProps<T>>(child)) {
      return React.cloneElement(child, { map: map ?? undefined, featuresCollection })
    }
    return child
  })


  return (<div> {markersWithProps} </div>)
}

type MapMarkerProps<T extends LocationData> = {
  map?: Map
  featuresCollection?: Collection<Feature>
  data: T
  latitude: string | number
  longitude: string | number
  label?: string
  markerClassName?: string
  onIconClick?: (data: T) => void
  iconColor?: string
  iconSize?: IconSizeProp
  mapLabel?: string
  popupComponent?: MarkerPopup<T>
}

/** Render a single icon on the map **/
export function MapMarker<T extends LocationData>({
                                                    map,
                                                    featuresCollection,
                                                    data,
                                                    latitude,
                                                    longitude,
                                                    label,
                                                    markerClassName,
                                                    onIconClick,
                                                    iconColor,
                                                    iconSize,
                                                    popupComponent,
                                                  }: MapMarkerProps<T>) {
  const ref = useRef<HTMLDivElement>(null)

  const handleClick = () => {
    if (onIconClick) {
      onIconClick(data)
    }
  }

  useEffect(() => {
    if (ref.current && map) {
      let pos = fromLonLat(MapUtils.longLatToCoord(longitude, latitude))
      const overlay = new Overlay({
        position: pos,
        positioning: 'bottom-center',
        element: ref.current,
        stopEvent: true,
        className: markerClassName,
      })
      map.addOverlay(overlay)
      return () => {
        map.removeOverlay(overlay)
      }
    }
  }, [longitude, latitude, map, markerClassName])


  useEffect(() => {
    if (map && featuresCollection) {
      const labelPos = new Point(fromLonLat(MapUtils.longLatToCoord(longitude, latitude)))
      const feature = new Feature({
        geometry: labelPos,
        name: label,
      })
      const labelStyle = new Style({
        text: new Text({
          font: '12px Calibri,sans-serif',
          overflow: true,
          fill: new Fill({
            color: '#007aff',
          }),
          stroke: new Stroke({
            color: '#fff',
            width: 3,
          }),
          offsetY: -40,
          text: feature.get('name'),
        }),
      })

      feature.setStyle(labelStyle)
      featuresCollection.push(feature)
      return () => {
        featuresCollection.remove(feature)
      }
    }
  }, [longitude, latitude, map, featuresCollection, label])

  const MarkerPopup = popupComponent
  const icon = <Icon link className={'mapIcon'} name="map marker"
                     style={{ color: iconColor }} size={iconSize} onClick={onIconClick && handleClick}/>
  const marker = MarkerPopup ? <MarkerPopup data={data} trigger={icon}/> : icon
  return (
    <div style={{ display: 'none' }}>
      <div ref={ref}>
        {marker}
      </div>
    </div>
  )
}